import { type UseFormReturn, useForm } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { fileToUrlByBase64 } from '@fukuri-admins/utils/common';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { CreatePortalFormFieldValues, createPortalSchema } from '../logics/schema';
import { usePostPortal } from './usePostPortal';

type Args = {
  lastSortOrder?: number;
  onClose: () => void;
};

type IUseCreatePortalModal = (args: Args) => {
  state: {
    methods: UseFormReturn<CreatePortalFormFieldValues>;
    isMutating: boolean;
    selectedFile: File | undefined;
  };
  actions: {
    handleSubmit: () => void;
    handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleRemoveFile: () => void;
    handleItemTypeChange: (value: 'url' | 'file') => void;
  };
};

export const useCreatePortalModal: IUseCreatePortalModal = ({ lastSortOrder, onClose }) => {
  const { showToast } = useToast();
  const threeLetterCode = getThreeLetterCode() || '';
  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);

  const methods = useForm<CreatePortalFormFieldValues>({
    resolver: zodResolver(createPortalSchema),
    criteriaMode: 'all',
    defaultValues: {
      title: '',
      url: '',
      file: undefined,
      icon_name: '',
      item_type: 'url',
      sort_order: lastSortOrder ? lastSortOrder + 1 : 1,
    },
  });

  const { trigger, isMutating } = usePostPortal(threeLetterCode, {
    onSuccess: () => {
      showToast({ message: 'ポータルを作成しました。', status: 'success' });
      onClose();
    },
    onError: () => {
      showToast({ message: 'ポータルの作成に失敗しました。', status: 'danger' });
    },
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      methods.setValue('file', file);
      methods.setValue('item_type', 'file');
      methods.clearErrors('url');
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(undefined);
    methods.setValue('file', undefined);
  };

  const handleItemTypeChange = (value: 'url' | 'file') => {
    methods.setValue('item_type', value);
    if (value === 'url') {
      setSelectedFile(undefined);
      methods.setValue('file', undefined);
      methods.clearErrors('file');
    } else {
      methods.clearErrors('url');
    }
  };

  const handleSubmit = methods.handleSubmit(async data => {
    const requestBody = {
      client_portal_item: {
        title: data.title,
        icon_name: data.icon_name,
        sort_order: data.sort_order,
        item_type: data.item_type,
        url: data.item_type === 'url' && data.url.trim() ? data.url.trim() : null,
        file: null,
      },
    };

    if (data.item_type === 'file' && data.file) {
      requestBody.client_portal_item.file = await fileToUrlByBase64(data.file);
    }

    await trigger(requestBody);
  });

  return {
    state: { methods, isMutating, selectedFile },
    actions: { handleSubmit, handleFileChange, handleRemoveFile, handleItemTypeChange },
  };
};
